import { Link } from 'react-router-dom'
import { services } from '@/services/registry'
import { useAuthStore } from '@/stores/auth'
import { ArrowIcon } from '@/components/icons'
import { TierBadge } from '@/components/TierBadge'
import { BetaBanner } from '@/components/BetaBanner'
import { trackEvent } from '@/lib/analytics'
import { NET_BEWUST_SLUG, programsEnabled } from '@/lib/programs'
import { ProgramBanner } from '@/components/programs/ProgramBanner'

const SPOTLIGHT_ID = 'battery-advice'

/**
 * Post-login dashboard: a photographic hero for the battery advice with the
 * other easyEnergy services as a compact list beside it.
 */
export function ServicesPage() {
  const user = useAuthStore((s) => s.userDetails?.user ?? null)

  const spotlight = services.find((s) => s.id === SPOTLIGHT_ID && s.available)
  const rest = services.filter((s) => s.id !== spotlight?.id && s.id !== 'more-soon')

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pb-6 pt-8">
      <BetaBanner />

      <h1 className="mt-8 text-28 font-semibold leading-111 lg:text-44">
        {user ? `Welkom, ${user.firstName}` : 'Welkom'}
      </h1>
      <p className="mt-3 max-w-xl text-base leading-normal text-dark-blue/70">
        Dit zijn je easyEnergy-diensten: persoonlijke rapporten en analyses op basis van de
        energiedata van je eigen huishouden. Powered by Chargee.
      </p>

      {/* The pilot first: it has a sign-up deadline, the other services do not. */}
      {programsEnabled && (
        <div className="mt-6">
          <ProgramBanner slug={NET_BEWUST_SLUG} />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        {/* Hero: the flagship battery advice on the spotlight photo */}
        {spotlight && (
          <Link
            to={spotlight.route}
            className="group relative flex min-h-[380px] flex-col justify-end overflow-hidden rounded-3xl lg:min-h-[460px]"
            onClick={() =>
              trackEvent('battery_advice_cta_click', {
                source: 'services_spotlight',
                service_id: 'battery_advice',
              })
            }
          >
            <img
              src="/thuisbatterij-spotlight.jpg"
              alt="Thuisbatterij"
              className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div
              className="absolute inset-0 bg-gradient-to-t from-dark-blue/85 via-dark-blue/30 to-transparent"
              aria-hidden="true"
            />
            <div className="relative z-10 p-6 md:p-10">
              {spotlight.tier && <TierBadge tier={spotlight.tier} />}
              <h2 className="mt-4 max-w-lg text-26 font-semibold leading-111 text-white lg:text-42">
                Ontdek wat een thuisbatterij jou oplevert
              </h2>
              <p className="mt-3 max-w-lg text-base leading-normal text-white/80">
                Doorgerekend op je eigen energiedata: beste capaciteit, besparing per jaar en
                terugverdientijd.
              </p>
              <span className="btn-primary mt-6 w-fit !px-7">
                Bereken mijn advies
                <ArrowIcon />
              </span>
            </div>
          </Link>
        )}

        {/* The other services, compact */}
        <div className="flex flex-col gap-4">
          {rest.map((service) =>
            service.available ? (
              <Link
                key={service.id}
                to={service.route}
                className="card-beige group flex flex-1 items-center justify-between gap-4 p-5 hover-opacity"
              >
                <div className="min-w-0">
                  {service.tier && (
                    <TierBadge tier={service.tier} className="!h-6 !px-2.5 !text-11" />
                  )}
                  <h3 className="mt-2 text-lg font-semibold text-dark-blue">{service.name}</h3>
                  <p className="mt-1 line-clamp-2 text-15 leading-normal text-dark-blue/60">
                    {service.description}
                  </p>
                </div>
                <span className="circle-btn">
                  <ArrowIcon />
                </span>
              </Link>
            ) : (
              <div
                key={service.id}
                aria-disabled="true"
                className="flex flex-1 select-none items-center justify-between gap-4 rounded-3xl bg-beige bg-card-gradient p-5 opacity-80"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {service.tier && (
                      <TierBadge tier={service.tier} className="!h-6 !px-2.5 !text-11" />
                    )}
                    <span className="inline-flex items-center rounded-full bg-dark-blue px-2.5 py-1 text-11 font-medium text-beige">
                      Binnenkort beschikbaar
                    </span>
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-dark-blue/70">{service.name}</h3>
                  <p className="mt-1 line-clamp-2 text-15 leading-normal text-dark-blue/50">
                    {service.description}
                  </p>
                </div>
                <span className="circle-btn !bg-beige-2 !text-dark-blue/40">
                  <ArrowIcon />
                </span>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}
